import { Smartphone, Settings, Eye, Printer, Lock, Loader2, Cloud, Check, Building2, ChevronDown, Plus } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';

export default function Header() {
  const {
    isStandalone,
    deferredPrompt,
    isIOS,
    isSafari,
    mode,
    setMode,
    handleInstallClick,
    setShowIOSGuide,
    logProposalActivity,
    saveProposalToCloud,
    readOnlyState,
    autoSaveStatus,
    proposalType,
    projectMeta,
    units,
    activeUnitId,
    setActiveUnitId,
    addUnit,
  } = useAppContext();

  const [unitSwitcherOpen, setUnitSwitcherOpen] = useState(false);
  const unitSwitcherRef = useRef<HTMLDivElement>(null);
  const activeUnit = units.find(u => u.id === activeUnitId);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (unitSwitcherRef.current && !unitSwitcherRef.current.contains(e.target as Node)) {
        setUnitSwitcherOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="no-print bg-[#18181B] text-white py-3 border-b border-stone-800/80 shadow-xs">
      <div className="w-full px-6 md:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        {/* LEFT: Proposal context — subtitle, PWA install, and (bulk mode) active daire tipi indicator */}
        <div className="flex flex-col items-start gap-1 shrink-0">
          <div className="flex items-center space-x-2">
            <img
              src="https://res.cloudinary.com/drp4askpq/image/upload/v1785956950/intema_logo_beyaz_slogansız_klzcxo.svg"
              alt="İntema"
              className="h-3.5 w-auto object-contain"
            />
            {!isStandalone && (deferredPrompt || (isIOS && isSafari)) ? (
              <button
                onClick={deferredPrompt ? handleInstallClick : () => setShowIOSGuide(true)}
                className="bg-[#945f46] hover:bg-[#83523c] text-white text-[9px] font-bold px-2 py-0.5 rounded shadow-xs flex items-center space-x-1 shrink-0 transition-colors"
                title="Uygulama Olarak Yükle"
              >
                <Smartphone className="w-2.5 h-2.5" />
                <span>YÜKLE</span>
              </button>
            ) : null}
          </div>
          <span className="text-xs font-medium text-stone-400 tracking-wide">Teklif ve Sunum Portalı</span>

          {proposalType === 'bulk' && mode === 'edit' && (
            <div className="relative" ref={unitSwitcherRef}>
              <button
                type="button"
                onClick={() => setUnitSwitcherOpen(v => !v)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-stone-900 border border-stone-750 text-[11px] font-semibold text-brand-300 hover:bg-stone-800 transition-colors"
              >
                <Building2 className="w-3 h-3 text-brand-400 shrink-0" />
                <span className="max-w-[160px] truncate">
                  {projectMeta.projectName || 'Toplu Proje'} — {activeUnit?.label || 'Daire Tipi Seçin'}
                </span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${unitSwitcherOpen ? 'rotate-180' : ''}`} />
              </button>

              {unitSwitcherOpen && units.length > 0 && (
                <div className="absolute left-0 top-full pt-1.5 w-56 z-[110] animate-in fade-in slide-in-from-top-1 duration-150">
                  <div className="bg-[#18181B] border border-stone-800 rounded-xl shadow-2xl py-1 overflow-hidden">
                    {units.map(unit => (
                      <button
                        key={unit.id}
                        type="button"
                        onClick={() => { setActiveUnitId(unit.id); setUnitSwitcherOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-xs flex items-center space-x-2 transition-colors cursor-pointer ${
                          unit.id === activeUnitId ? 'text-white bg-stone-800/80 font-semibold' : 'text-stone-300 hover:text-white hover:bg-stone-800/50'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${unit.id === activeUnitId ? 'bg-brand-400' : 'bg-stone-600'}`} />
                        <span className="truncate">{unit.label || 'İsimsiz Daire Tipi'}</span>
                      </button>
                    ))}
                    {!readOnlyState.isReadOnly && (
                      <button
                        type="button"
                        onClick={() => { addUnit(); setUnitSwitcherOpen(false); }}
                        className="w-full text-left px-4 py-2 text-xs flex items-center space-x-2 transition-colors cursor-pointer text-brand-400 hover:text-brand-300 hover:bg-stone-800/60 border-t border-stone-800 mt-1 pt-2.5"
                      >
                        <Plus className="w-3 h-3 shrink-0" />
                        <span className="font-semibold">Yeni Daire Tipi Ekle</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Mode switch + Save + (preview) Print — hidden on the welcome screen, no proposal is active there */}
        {mode !== 'welcome' && (
        <div className="flex items-center gap-5 shrink-0">
          <div className="bg-stone-900/90 p-1 rounded-lg border border-stone-800 flex items-center space-x-1">
            <button
              type="button"
              onClick={() => setMode('edit')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center space-x-1.5 transition-all ${
                mode === 'edit'
                  ? 'bg-[#945f46] text-white shadow-xs font-semibold'
                  : 'text-stone-400 hover:text-stone-200 hover:bg-stone-800/50'
              }`}
            >
              <Settings className="w-3.5 h-3.5" />
              <span>Düzenle</span>
            </button>
            <button
              type="button"
              onClick={() => setMode('preview')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center space-x-1.5 transition-all ${
                mode === 'preview'
                  ? 'bg-[#945f46] text-white shadow-xs font-semibold'
                  : 'text-stone-400 hover:text-stone-200 hover:bg-stone-800/50'
              }`}
            >
              <Eye className="w-3.5 h-3.5" />
              <span>Önizleme</span>
            </button>
          </div>

          <div className="h-4 w-px bg-stone-800 shrink-0" />

          <button
            type="button"
            onClick={() => saveProposalToCloud(false)}
            disabled={readOnlyState.isReadOnly || autoSaveStatus === 'saving'}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center space-x-1.5 transition-all border ${
              readOnlyState.isReadOnly
                ? 'bg-stone-800/50 text-stone-500 border-stone-800 cursor-not-allowed opacity-60'
                : autoSaveStatus === 'saving'
                ? 'bg-[#945f46]/30 text-amber-200 border-[#945f46]/50 cursor-wait'
                : autoSaveStatus === 'unsaved'
                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 hover:bg-amber-500/30'
                : 'bg-stone-800/80 text-stone-300 border-stone-700/70 hover:bg-stone-750'
            }`}
            title={readOnlyState.isReadOnly ? 'Salt okunur teklif kaydedilemez.' : 'Mevcut teklifi buluta kaydet'}
          >
            {readOnlyState.isReadOnly ? (
              <Lock className="w-3.5 h-3.5 text-stone-500" />
            ) : autoSaveStatus === 'saving' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-300" />
            ) : autoSaveStatus === 'unsaved' ? (
              <Cloud className="w-3.5 h-3.5 text-amber-400" />
            ) : (
              <Check className="w-3.5 h-3.5 text-emerald-400" />
            )}
            <span>
              {autoSaveStatus === 'saving'
                ? 'Kaydediliyor...'
                : autoSaveStatus === 'unsaved'
                ? 'Kaydet'
                : 'Kaydedildi'}
            </span>
          </button>

          {mode === 'preview' && (
            <button
              type="button"
              onClick={async () => {
                logProposalActivity('print_pdf');
                const images = Array.from(document.querySelectorAll('.print-container img')) as HTMLImageElement[];
                await Promise.all([
                  document.fonts.ready,
                  ...images.map(img => {
                    if (img.complete) return Promise.resolve();
                    return new Promise<void>(resolve => {
                      img.addEventListener('load', () => resolve(), { once: true });
                      img.addEventListener('error', () => resolve(), { once: true });
                    });
                  }),
                ]);
                window.print();
              }}
              className="bg-[#945f46] hover:bg-[#83523c] text-white px-3.5 py-1.5 text-xs font-semibold rounded-lg flex items-center space-x-1.5 transition-all shadow-md hover:scale-[1.02] active:scale-[0.98]"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Yazdır</span>
            </button>
          )}
        </div>
        )}
      </div>
    </header>
  );
}
